import React, { useEffect, useRef, useState } from 'react'
import { EditorState, StateEffect, StateField, Prec } from '@codemirror/state'
import { EditorView, keymap, lineNumbers, drawSelection, highlightActiveLine, highlightActiveLineGutter, dropCursor, Decoration, type DecorationSet } from '@codemirror/view'
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands'
import { javascript } from '@codemirror/lang-javascript'
import { python } from '@codemirror/lang-python'
import { css } from '@codemirror/lang-css'
import { html } from '@codemirror/lang-html'
import { json } from '@codemirror/lang-json'
import { markdown } from '@codemirror/lang-markdown'
import { rust } from '@codemirror/lang-rust'
import { autocompletion, closeBrackets } from '@codemirror/autocomplete'
import { bracketMatching, foldGutter, indentOnInput, syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language'
import { search } from '@codemirror/search'
import { useAppStore } from '../../store/appStore'
import { pulseTheme } from './pulseTheme'
import { ghostTextExtension } from './ghostText'
import { snippetCompletions, loadInstalledExtensions } from '../../services/extensionRuntime'
import { InlineEditOverlay } from './InlineEdit'
import { apiUrl, authFetch } from '../../lib/api'
import { PairProgrammingPanel } from './PairProgrammingPanel'

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'ico', 'webp', 'bmp'])

// Load installed extensions on startup
loadInstalledExtensions()

// ── Diff highlighting extension ──────────────────────────────
const setHighlights = StateEffect.define<{ green: number[]; orange: number[] }>()
const clearHighlights = StateEffect.define<null>()

const greenDeco = Decoration.line({ class: 'cm-diff-green' })
const orangeDeco = Decoration.line({ class: 'cm-diff-orange' })

const diffField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(value, tr) {
    for (const e of tr.effects) {
      if (e.is(setHighlights)) {
        const decos: any[] = []
        const doc = tr.state.doc
        for (const ln of e.value.green) {
          if (ln >= 1 && ln <= doc.lines) decos.push(greenDeco.range(doc.line(ln).from))
        }
        for (const ln of e.value.orange) {
          if (ln >= 1 && ln <= doc.lines) decos.push(orangeDeco.range(doc.line(ln).from))
        }
        return Decoration.set(decos.sort((a, b) => a.from - b.from))
      }
      if (e.is(clearHighlights)) return Decoration.none
    }
    return value
  },
  provide: (f) => EditorView.decorations.from(f),
})

function diffLines(oldText: string, newText: string): number[] {
  return agentPatchHighlights(oldText, newText).green
}

function agentPatchHighlights(oldText: string, newText: string): { green: number[]; orange: number[] } {
  const oldLines = oldText.split('\n')
  const newLines = newText.split('\n')
  const green: number[] = []
  // Simple: a line is "added" if it exists in new but not in old (by exact match)
  const oldSet = new Set(oldLines)
  for (let i = 0; i < newLines.length; i++) {
    if (!oldSet.has(newLines[i])) {
      green.push(i + 1)
    }
  }
  return { green, orange: [] }
}

function applyAgentPatchHighlights(view: EditorView | null, tabPath: string) {
  if (!view || view.destroyed) return
  const patch = useAppStore.getState().agentFilePatches[tabPath]
  if (!patch) return
  const { green } = agentPatchHighlights(patch.oldContent, patch.newContent)
  if (green.length === 0) return
  view.dispatch({ effects: setHighlights.of({ green, orange: [] }) })
}

// ── Main components ──────────────────────────────────────────

function SplitIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="3" x2="12" y2="21" />
    </svg>
  )
}

export function EditorArea({ hidden }: { hidden?: boolean }) {
  const { openTabs, activeTabId, closeTab, setActiveTab, markDirty, openTab } = useAppStore()
  const activeTab = openTabs.find((t) => t.id === activeTabId)
  const [splitTabId, setSplitTabId] = useState<string | null>(null)
  const [dragIdx, setDragIdx] = useState<number | null>(null)

  // Listen for table open events from Database panel
  useEffect(() => {
    const handler = (e: Event) => {
      const { dbName, tableName, columns, rows } = (e as CustomEvent).detail ?? {}
      if (!tableName) return
      const tabId = `table:${dbName}:${tableName}`
      openTab({
        id: tabId,
        path: tabId,
        name: `${tableName} (${dbName})`,
        language: 'table',
        content: JSON.stringify({ columns, rows }),
      })
    }
    window.addEventListener('pulse:open-table', handler)
    return () => window.removeEventListener('pulse:open-table', handler)
  }, [openTab])

  if (hidden) return null

  const splitTab = splitTabId ? openTabs.find((t) => t.id === splitTabId) : null
  const ext = activeTab?.language ?? ''
  const isImage = IMAGE_EXTS.has(ext)
  const isMd = ext === 'md'

  const onDragStart = (e: React.DragEvent, idx: number) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move' }
  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault()
    if (dragIdx === null || dragIdx === idx) return
    const tabs = [...useAppStore.getState().openTabs]
    const [moved] = tabs.splice(dragIdx, 1)
    tabs.splice(idx, 0, moved)
    useAppStore.setState({ openTabs: tabs })
    setDragIdx(idx)
  }

  return (
    <div className="editor-area">
      <div className="editor-tabs">
        {openTabs.map((tab, i) => (
          <div key={tab.id} className={`editor-tab ${activeTabId === tab.id ? 'editor-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)} draggable
            onDragStart={(e) => onDragStart(e, i)} onDragOver={(e) => onDragOver(e, i)} onDragEnd={() => setDragIdx(null)}
            style={{ opacity: dragIdx === i ? 0.4 : 1 }}>
            <LangDot lang={tab.language} />
            <span className="editor-tab__name">{tab.name}</span>
            {tab.isDirty && <span className="editor-tab__dirty" />}
            <button className="editor-tab__close" onClick={(e) => { e.stopPropagation(); closeTab(tab.id) }}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        ))}
        {openTabs.length > 1 && (
          <button title={splitTab ? 'Close split' : 'Split editor'}
            onClick={() => setSplitTabId(splitTab ? null : openTabs.find((t) => t.id !== activeTabId)?.id ?? null)}
            style={{ margin: '0 4px', padding: '4px', display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', color: splitTab ? 'var(--accent)' : 'var(--text-2)' }}>
            <SplitIcon />
          </button>
        )}
        {openTabs.length === 0 && <div className="editor-tabs__empty">Open a file from the sidebar</div>}
      </div>

      {activeTab && (
        <div style={{ padding: '3px 14px', fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, background: 'var(--bg-1)' }}>
          {activeTab.path.replace(/^\//, '').split('/').map((seg, i, arr) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color: 'var(--text-2)', fontSize: 8 }}>&rsaquo;</span>}
              <span style={{ color: i === arr.length - 1 ? 'var(--text-0)' : 'var(--text-2)' }}>{seg}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {activeTab ? (
          activeTab.language === 'table' ? <TableViewer content={activeTab.content} /> :
          isImage ? <ImagePreview path={activeTab.path} /> :
          isMd ? (
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
              <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                <CodeEditor key={activeTab.id} tabId={activeTab.id} content={activeTab.content} language={activeTab.language} onChange={(c) => markDirty(activeTab.id, c)} onSave={() => useAppStore.getState().saveTab(activeTab.id)} />
              </div>
              <div style={{ width: 1, background: 'var(--border)', flexShrink: 0 }} />
              <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--bg-2)' }}><MarkdownPreview content={activeTab.content} /></div>
            </div>
          ) : (
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <CodeEditor key={activeTab.id} tabId={activeTab.id} content={activeTab.content} language={activeTab.language} onChange={(c) => markDirty(activeTab.id, c)} onSave={() => useAppStore.getState().saveTab(activeTab.id)} />
            </div>
          )
        ) : null}

        {splitTab && !isImage && (
          <>
            <div style={{ width: 1, background: 'var(--border)', flexShrink: 0 }} />
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ padding: '3px 14px', fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{splitTab.name}</span>
                <button onClick={() => setSplitTabId(null)} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', display: 'flex' }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                <CodeEditor key={`split-${splitTab.id}`} tabId={splitTab.id} content={splitTab.content} language={splitTab.language} onChange={(c) => markDirty(splitTab.id, c)} onSave={() => useAppStore.getState().saveTab(splitTab.id)} />
              </div>
            </div>
          </>
        )}
      </div>
      <PairProgrammingPanel />
    </div>
  )
}

// ── CodeEditor with diff highlighting ────────────────────────
function CodeEditor({ content, language, onChange, onSave, tabId }: {
  content: string; language: string; onChange: (val: string) => void; onSave: () => void; tabId: string
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave
  const lastContentRef = useRef(content)
  const [inlineEdit, setInlineEdit] = useState<{ code: string; from: number; to: number; pos: { top: number; left: number } } | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        const newContent = update.state.doc.toString()
        onChangeRef.current(newContent)
      }
    })

    const state = EditorState.create({
      doc: content,
      extensions: [
        history(), lineNumbers(), foldGutter(), drawSelection(), dropCursor(),
        highlightActiveLine(), highlightActiveLineGutter(), indentOnInput(),
        bracketMatching(), closeBrackets(), autocompletion({ override: [snippetCompletions] }),
        search({ top: true }),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        getLanguageExtension(language),
        ...(language === 'diff' ? [EditorState.readOnly.of(true)] : []),
        keymap.of([
          { key: 'Mod-s', run: () => { onSaveRef.current(); return true } },
          { key: 'Mod-k', run: (view) => {
            const sel = view.state.selection.main
            if (sel.empty) return false
            const selectedText = view.state.sliceDoc(sel.from, sel.to)
            const coords = view.coordsAtPos(sel.from)
            if (coords) setInlineEdit({ code: selectedText, from: sel.from, to: sel.to, pos: { top: coords.bottom + 4, left: coords.left } })
            return true
          }},
          ...defaultKeymap, ...historyKeymap, indentWithTab,
        ]),
        ...ghostTextExtension(),
        diffField,
        pulseTheme, updateListener, EditorState.tabSize.of(2),
      ],
    })

    const view = new EditorView({ state, parent: containerRef.current })
    viewRef.current = view
    lastContentRef.current = content
    applyAgentPatchHighlights(view, tabId)

    const onAgentPatch = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.path === tabId) applyAgentPatchHighlights(view, tabId)
    }
    window.addEventListener('pulse:agent-file-patched', onAgentPatch)

    const syncHeight = () => {
      if (!containerRef.current) return
      const h = containerRef.current.clientHeight
      if (h > 0) view.dom.style.height = h + 'px'
    }
    syncHeight()
    const ro = new ResizeObserver(syncHeight)
    ro.observe(containerRef.current)

    // Poll for external content changes (from AI apply or collab)
    const pollInterval = setInterval(() => {
      if (!view || view.destroyed) return
      const tab = useAppStore.getState().openTabs.find((t) => t.id === tabId)
      if (!tab) return
      const editorContent = view.state.doc.toString()

      // Check for remote collab change
      const remote = (window as any).__pulseRemoteChange
      if (remote && remote.tabId === tabId) {
        delete (window as any).__pulseRemoteChange
        if (remote.content !== editorContent) {
          const oldContent = editorContent
          view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: remote.content } })
          const changed = diffLines(oldContent, remote.content)
          if (changed.length > 0 && changed.length < 100) {
            view.dispatch({ effects: setHighlights.of({ green: [], orange: changed }) })
            setTimeout(() => { if (!view.destroyed) view.dispatch({ effects: clearHighlights.of(null) }) }, 15000)
          }
          lastContentRef.current = remote.content
        }
        return
      }

      // Check for AI apply change (store content differs from editor)
      if (tab.content !== editorContent && tab.content !== lastContentRef.current) {
        const oldContent = editorContent
        view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: tab.content } })
        const patch = useAppStore.getState().agentFilePatches[tabId]
        const highlights = patch
          ? agentPatchHighlights(patch.oldContent, patch.newContent)
          : { green: diffLines(oldContent, tab.content), orange: [] as number[] }
        if (highlights.green.length > 0 && highlights.green.length < 100) {
          view.dispatch({ effects: setHighlights.of(highlights) })
        }
        lastContentRef.current = tab.content
      }
    }, 300)

    return () => {
      window.removeEventListener('pulse:agent-file-patched', onAgentPatch)
      clearInterval(pollInterval)
      ro.disconnect()
      view.destroy()
      viewRef.current = null
    }
  }, [language, tabId])

  const handleInlineApply = (newCode: string) => {
    const view = viewRef.current
    if (!view || !inlineEdit) return
    const oldContent = view.state.doc.toString()
    view.dispatch({ changes: { from: inlineEdit.from, to: inlineEdit.to, insert: newCode } })
    const changed = diffLines(oldContent, view.state.doc.toString())
    if (changed.length > 0) {
      view.dispatch({ effects: setHighlights.of({ green: changed, orange: [] }) })
      setTimeout(() => { if (!view.destroyed) view.dispatch({ effects: clearHighlights.of(null) }) }, 15000)
    }
    setInlineEdit(null)
  }

  return (
    <>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        <div ref={containerRef} className="codemirror-host" style={{ flex: 1 }} />
        <Minimap content={content} viewRef={viewRef} />
      </div>
      {inlineEdit && (
        <InlineEditOverlay selectedCode={inlineEdit.code} fullContent={content} language={language}
          position={inlineEdit.pos} onApply={handleInlineApply} onClose={() => setInlineEdit(null)} />
      )}
    </>
  )
}

// ── Helpers ──────────────────────────────────────────────────

// ── Minimap (VS Code style) ────────────────────────────────────
function Minimap({ content, viewRef }: { content: string; viewRef: React.RefObject<EditorView | null> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const mContainerRef = useRef<HTMLDivElement>(null)
  const [viewportTop, setViewportTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(50)
  const [visible, setVisible] = useState(true)
  const [hovered, setHovered] = useState(false)
  const totalLines = content.split('\n').length
  const LINE_H = 2

  useEffect(() => {
    const canvas = canvasRef.current
    const container = mContainerRef.current
    if (!canvas || !container) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    // Set canvas size to match container
    const h = container.clientHeight
    const w = 60
    canvas.width = w
    canvas.height = h
    ctx.clearRect(0, 0, w, h)
    const lines = content.split('\n')
    const lineH = h / lines.length
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      if (!line.trim()) continue
      const indent = line.search(/\S/)
      const len = Math.min(line.length, 80)
      const y = i * lineH
      const isComment = line.trimStart().startsWith('//') || line.trimStart().startsWith('#')
      const isKeyword = /^\s*(import|export|const|let|var|function|class|if|else|return|async|await)\b/.test(line)
      ctx.fillStyle = isComment ? 'rgba(95,212,164,0.3)' : isKeyword ? 'rgba(124,110,245,0.5)' : 'rgba(255,255,255,0.25)'
      ctx.fillRect(indent * 0.6, y, Math.max((len - indent) * 0.6, 4), Math.max(lineH - 0.5, 1))
    }
  }, [content, visible])

  useEffect(() => {
    let scrollListener: (() => void) | null = null
    let scroller: HTMLElement | null = null
    const interval = setInterval(() => {
      const view = viewRef.current
      if (!view) return
      const s = view.scrollDOM
      const container = mContainerRef.current
      if (!container || s.scrollHeight <= 0) return
      const mapH = container.clientHeight
      setViewportTop((s.scrollTop / s.scrollHeight) * mapH)
      setViewportHeight(Math.max((s.clientHeight / s.scrollHeight) * mapH, 20))
      // Attach scroll listener once
      if (!scrollListener) {
        scroller = s
        scrollListener = () => {
          const c = mContainerRef.current
          if (!c || s.scrollHeight <= 0) return
          const mH = c.clientHeight
          setViewportTop((s.scrollTop / s.scrollHeight) * mH)
          setViewportHeight(Math.max((s.clientHeight / s.scrollHeight) * mH, 20))
        }
        s.addEventListener('scroll', scrollListener)
      }
    }, 200)
    return () => { clearInterval(interval); if (scroller && scrollListener) scroller.removeEventListener('scroll', scrollListener) }
  }, [content])

  const handleClick = (e: React.MouseEvent) => {
    const view = viewRef.current
    const container = mContainerRef.current
    if (!view || !container) return
    const rect = container.getBoundingClientRect()
    const ratio = (e.clientY - rect.top) / container.clientHeight
    view.scrollDOM.scrollTop = ratio * view.scrollDOM.scrollHeight - view.scrollDOM.clientHeight / 2
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const view = viewRef.current
    const container = mContainerRef.current
    if (!view || !container) return
    const rect = container.getBoundingClientRect()
    const onMove = (ev: MouseEvent) => {
      const ratio = (ev.clientY - rect.top) / container.clientHeight
      view.scrollDOM.scrollTop = ratio * view.scrollDOM.scrollHeight - view.scrollDOM.clientHeight / 2
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    // Also scroll on initial click
    const ratio = (e.clientY - rect.top) / container.clientHeight
    view.scrollDOM.scrollTop = ratio * view.scrollDOM.scrollHeight - view.scrollDOM.clientHeight / 2
  }

  if (totalLines < 40) return null

  if (!visible) {
    return (
      <div
        onClick={() => setVisible(true)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ width: 24, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer', background: hovered ? 'var(--bg-3)' : 'var(--bg-1)', borderLeft: '1px solid var(--border)', transition: 'all 0.15s' }}
        title="Show minimap"
      >
        <span style={{ fontSize: 14, color: hovered ? 'var(--accent)' : 'var(--text-2)', transition: 'color 0.15s' }}>◀</span>
        <span style={{ fontSize: 9, color: hovered ? 'var(--text-0)' : 'var(--text-2)', writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '0.1em', fontFamily: 'var(--font-mono)', fontWeight: 700, transition: 'color 0.15s' }}>MINIMAP</span>
      </div>
    )
  }

  return (
    <div ref={mContainerRef} onMouseDown={handleMouseDown} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ width: 60, flexShrink: 0, position: 'relative', cursor: 'pointer', background: 'var(--bg-1)', borderLeft: '1px solid var(--border)', overflow: 'hidden' }}>
      {/* Hide button */}
      {hovered && (
        <div
          onClick={(e) => { e.stopPropagation(); setVisible(false) }}
          style={{ position: 'absolute', top: 4, right: 4, zIndex: 2, background: 'var(--bg-3)', border: '1px solid var(--border-2)', borderRadius: 4, padding: '2px 5px', fontSize: 9, color: 'var(--text-1)', cursor: 'pointer', fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: 3 }}
          title="Hide minimap"
        >▶</div>
      )}
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      <div style={{ position: 'absolute', top: viewportTop, left: 0, right: 0, height: viewportHeight, background: 'rgba(124,110,245,0.12)', border: '1px solid rgba(124,110,245,0.3)', borderRadius: 2, pointerEvents: 'none' }} />
    </div>
  )
}

function LangDot({ lang }: { lang: string }) {
  const colors: Record<string, string> = {
    tsx: '#61dafb', ts: '#3178c6', js: '#f7df1e', jsx: '#61dafb',
    json: '#f5d76e', css: '#c07ef5', html: '#e34c26', md: '#9395b0',
    py: '#3572a5', rs: '#ce422b', env: '#f5a157',
    png: '#5fd4a4', jpg: '#5fd4a4', jpeg: '#5fd4a4', gif: '#5fd4a4', svg: '#f5a157',
  }
  return <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: colors[lang] ?? 'var(--text-2)', display: 'inline-block' }} />
}

// Auto-decorate diff files with green/red per line
const diffLineHighlighter = EditorView.decorations.compute(['doc'], (state) => {
  const decos: any[] = []
  for (let i = 1; i <= state.doc.lines; i++) {
    const line = state.doc.line(i)
    const text = line.text
    if (text.startsWith('+ ') || (text.startsWith('+') && !text.startsWith('++'))) {
      decos.push(greenDeco.range(line.from))
    } else if (text.startsWith('- ') || (text.startsWith('-') && !text.startsWith('--'))) {
      decos.push(orangeDeco.range(line.from))
    }
  }
  return Decoration.set(decos)
})

function getLanguageExtension(lang: string) {
  switch (lang) {
    case 'ts': case 'tsx': return javascript({ typescript: true, jsx: lang === 'tsx' })
    case 'js': case 'jsx': return javascript({ jsx: lang === 'jsx' })
    case 'py': return python()
    case 'css': return css()
    case 'html': return html()
    case 'json': return json()
    case 'md': return markdown()
    case 'rs': return rust()
    case 'diff': return diffLineHighlighter
    default: return javascript()
  }
}

function MarkdownPreview({ content }: { content: string }) {
  const rendered = content
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3 style="color:var(--accent);margin:16px 0 8px;font-size:15px">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 style="color:var(--accent);margin:16px 0 8px;font-size:17px">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 style="color:var(--accent);margin:16px 0 8px;font-size:20px">$1</h1>')
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre style="background:var(--bg-0);border:1px solid var(--border);border-radius:6px;padding:10px;font-family:var(--font-mono);font-size:11px;color:var(--accent-2)">$2</pre>')
    .replace(/`([^`]+)`/g, '<code style="background:var(--bg-3);padding:1px 5px;border-radius:3px;font-family:var(--font-mono);font-size:11px">$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:var(--accent)" target="_blank">$1</a>')
    .replace(/^- (.+)$/gm, '<li style="margin-left:16px">$1</li>')
    .replace(/\n\n/g, '<br/><br/>').replace(/\n/g, '<br/>')
  return <div className="md-preview" style={{ fontFamily: 'var(--font-sans)', fontSize: 13, lineHeight: 1.7, color: 'var(--text-0)' }} dangerouslySetInnerHTML={{ __html: rendered }} />
}

function ImagePreview({ path: filePath }: { path: string }) {
  const relPath = filePath.replace(/^\//, '')
  let token = ''
  try { token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? '' } catch {}
  const src = apiUrl(`/api/files/preview/${relPath}?token=${token}`)
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-2)', overflow: 'auto', padding: 24 }}>
      <div style={{ textAlign: 'center' }}>
        <img src={src} alt={relPath} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{relPath}</div>
      </div>
    </div>
  )
}

function TableViewer({ content }: { content: string }) {
  let columns: string[] = []
  let rows: any[] = []
  try {
    const data = JSON.parse(content)
    columns = data.columns ?? []
    rows = data.rows ?? []
  } catch {}

  return (
    <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-2)', padding: 0 }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-1)' }}>
        <span style={{ fontSize: 14 }}>📊</span>
        <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-0)' }}>{rows.length} rows · {columns.length} columns</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid var(--border)', color: 'var(--accent)', fontWeight: 700, position: 'sticky', top: 0, background: 'var(--bg-1)', whiteSpace: 'nowrap' }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-3)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}>
              {columns.map((c) => (
                <td key={c} style={{ padding: '6px 12px', color: 'var(--text-1)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row[c] === null ? <span style={{ color: 'var(--text-2)', fontStyle: 'italic' }}>NULL</span> : String(row[c])}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)' }}>No rows</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
